import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@store/store";
import { fetchSeekClarificationDetailsApplicantApi } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import { useNavigate } from "react-router-dom";
import { setActiveStep } from "@modules/stepper/redux/stepperSlice";
import { resetApplicationSubmitted } from "@modules/application-section/application-details/redux/applicationSubmittedSlice";
import ProvideClarificationDrawer from "./ProvideClarificationApplicant";
import { openFileBySignedUrl } from "../../utils/getViewSignedUrlHandler";
import { getObjectKey } from "@components/buttonGroupView/utils";
import {
  getFileROWSignedURLThunk,
  getFileSignedURLMorthThunk,
  getFileSignedURLThunk,
  getROWFileSignedURLMorthThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import { setClarificationData } from "./redux/clarificationSlice";

interface SenderDetails {
  sender_id: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  sender_role: string;
}

interface DocumentToUpdate {
  document_id: number;
  document_name: string;
  document_code: string;
  application_type_id: number;
  document_type: string;
  media_type: string;
  upload_required: boolean;
  sequence: number;
}

export interface ClarificationRecord {
  id: number;
  application_id: string;
  message: string;
  forwareded_message: string | null;
  date_time: string;
  sender_role: string;
  recipient_role: string;
  sender_details: SenderDetails;
  documents_to_updated: DocumentToUpdate[];
  documents?: Document[];
  questions_to_updated?: any[];
}

interface ClarificationCard {
  id: number;
  label: string;
  date: string;
  count: number;
  role: string;
  conversations: ClarificationRecord[];
}

interface Document {
  document_id: number;
  fileName: string;
  original_file_name: string;
  objectKey: string;
  file_size: number;
  mime_type: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  accessId: string | number;
  access?: boolean;
  row?: boolean;
  fetchAccess?: (id: any) => void;
  fetchRow?: (id: any) => void;
};

const SeekClarificationTray: React.FC<Props> = ({
  open,
  onClose,
  accessId,
  access,
  row,
  fetchAccess,
  fetchRow,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<ClarificationRecord[]>([]);

  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [provideClarificationOpen, setProvideClarificationOpen] =
    useState(false);

  const handleViewFile = (doc_name: any) => {
    openFileBySignedUrl({
      document_name: getObjectKey(doc_name),
      dispatch,
      getSignedUrlThunk: doc_name.includes("noc")
        ? access
          ? getFileSignedURLThunk
          : getFileROWSignedURLThunk
        : access
          ? getFileSignedURLMorthThunk
          : getROWFileSignedURLMorthThunk,
    });
  };

  useEffect(() => {
    const loadDetails = async () => {
      if (!open || !accessId) return;

      try {
        setIsLoading(true);

        const response = await dispatch(
          fetchSeekClarificationDetailsApplicantApi(accessId),
        );

        setItems(response?.payload?.data || []);
      } catch (error) {
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadDetails();
  }, [dispatch, open, accessId]);

  const getFullName = (sender?: SenderDetails) =>
    [sender?.first_name, sender?.middle_name, sender?.last_name]
      .filter(Boolean)
      .join(" ");

  const getInitials = (sender?: SenderDetails) =>
    `${sender?.first_name?.[0] ?? ""}${sender?.last_name?.[0] ?? ""}`;

  const getCardLabel = (senderRole?: string, recipientRole?: string) =>
    `Clarification Requested from ${senderRole}`;

  const lastConversation =
    selectedCard?.conversations?.[selectedCard.conversations.length - 1];

  const documentUpdateRequest = [...(selectedCard?.conversations || [])]
    .reverse()
    .find((item: ClarificationRecord) => item.documents_to_updated?.length > 0);

  const applicantRespondedAfterDocumentRequest =
    documentUpdateRequest &&
    selectedCard?.conversations?.some(
      (item: ClarificationRecord) =>
        item.sender_role === "Applicant" &&
        new Date(item.date_time).getTime() >
        new Date(documentUpdateRequest.date_time).getTime(),
    ); 
  const lastConversationHasDocumentRequest =
    lastConversation?.documents_to_updated?.length > 0;

  const lastConversationHasQuestionRequest =
    lastConversation?.questions_to_updated;

  const showUpdateDocuments =
    lastConversationHasDocumentRequest &&
    lastConversation?.sender_role !== "Applicant";

  const showUpdateQuestions =
    lastConversationHasQuestionRequest &&
    lastConversation?.sender_role !== "Applicant";

  const showSubmitCompliance =
    (lastConversationHasDocumentRequest ||
      lastConversationHasQuestionRequest) &&
    lastConversation?.sender_role !== "Applicant";

  const showProvideClarification =
    !lastConversationHasDocumentRequest &&
    !lastConversationHasQuestionRequest &&
    lastConversation?.sender_role !== "Applicant";



  const cards = useMemo<ClarificationCard[]>(() => {
    const grouped: Record<string, ClarificationRecord[]> = {};

    items.forEach((item) => {
      let threadRole = item.sender_role;

      // Applicant replies belong to the authority thread
      if (item.sender_role === "Applicant") {
        threadRole = item.recipient_role;
      }

      if (!grouped[threadRole]) {
        grouped[threadRole] = [];
      }

      grouped[threadRole].push(item);
    });

    return Object.entries(grouped)
      .filter(([role]) => role !== "Applicant")
      .map(([role, conversations]) => ({
        id: conversations[0].id,
        role,
        label: `Clarification Requested from ${role}`,
        date: dayjs(conversations[conversations.length - 1].date_time).format(
          "DD MMM YYYY",
        ),
        count: conversations.length,
        conversations: conversations.sort(
          (a, b) =>
            new Date(a.date_time).getTime() - new Date(b.date_time).getTime(),
        ),
      }));
  }, [items]);

  useEffect(() => {
    if (!open) {
      setSelectedCard(null);
    }
  }, [open]);

  useEffect(() => {
    if (selectedCard) {
      const updatedCard = cards.find((card) => card.role === selectedCard.role);

      if (updatedCard) {
        setSelectedCard(updatedCard);
      }
    }
  }, [cards]);

  const handleCloseTray = () => {
    setSelectedCard(null);
    onClose();
  };


  const handleComplianceSubmit = (clarification: ClarificationRecord) => {

    const QuestionsUpdateCheck = clarification?.questions_to_updated;
    // const documentRequestNullCheck = clarification?.documents_to_updated?.length > 0;

    dispatch(setClarificationData(clarification)); // 👈 store in redux

    // dispatch(setActiveStep(2));
    dispatch(resetApplicationSubmitted());

    if (QuestionsUpdateCheck) {
      navigate(
        `/update-questions?${access ? `access=${accessId}` : `row=${accessId}`}`,
      );


      // dispatch(setActiveStep(1));
      // dispatch(resetApplicationSubmitted());
      // navigate("/stepper");

      // navigate("/stepper-row");
    } else {
      navigate(
        `/update-documents?${access ? `access=${accessId}` : `row=${accessId}`}`,
      );
    }


  };

  // const documentRequestNullCheck = 
  // const QuestionsUpdateCheck= 


  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCloseTray}
      sx={{
        "& .MuiDrawer-paper": {
          width: "500px",
          // height: "1024px",
          maxWidth: "100%",
          backgroundColor: "#FFFFFF",
          opacity: 1,
          // left: "910px",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          borderBottomLeftRadius: "8px",
          transform: "rotate(0deg)",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: "#fff",
            display: "flex",
            alignItems: selectedCard ? "flex-start" : "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: selectedCard ? "flex-start" : "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            {selectedCard && (
              <IconButton onClick={() => setSelectedCard(null)} size="small">
                <ArrowBackRoundedIcon
                  sx={{
                    width: 18,
                    height: 18,
                    opacity: 1,
                    position: "relative",
                    color: "#000000",
                    top: "-2px",
                    left: "4px",
                    transform: "rotate(0deg)",
                  }}
                />
              </IconButton>
            )}
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontStyle: "normal",
                fontSize: selectedCard ? "18px" : "20px",
                color: "#212121",
                lineHeight: selectedCard ? "24px" : "normal",
                letterSpacing: selectedCard ? "-0.005em" : "normal",
                verticalAlign: selectedCard ? "bottom" : "baseline",
                textAlign: "left",
                whiteSpace: selectedCard ? "normal" : "nowrap",
                wordBreak: "break-word",
                maxWidth: selectedCard ? "360px" : "none",
              }}
            >
              {selectedCard ? selectedCard.label : "Clarifications"}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseTray} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : selectedCard ? (
            <Box>
              {selectedCard?.conversations?.map(
                (clarification: ClarificationRecord) => {
                  const sender = clarification.sender_details;

                  return (
                    <Box key={clarification.id} sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: "#CC8801",
                              fontSize: "10px",
                              fontWeight: 700,
                            }}
                          >
                            {getInitials(sender)}
                          </Avatar>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 700,
                              }}
                            >
                              {getFullName(sender)}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#7A7A7A",
                              }}
                            >
                              {sender?.sender_role}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "#7A7A7A",
                          }}
                        >
                          {dayjs(clarification.date_time).format(
                            "DD MMM YYYY, hh:mm A",
                          )}
                        </Typography>
                      </Box>

                      <Box sx={{ ml: "50px" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#212121",
                            mb: 2,
                          }}
                        >
                          {clarification.message}
                        </Typography>

                        {clarification.forwareded_message && (
                          <Box
                            sx={{
                              bgcolor: "#F7F7F7",
                              borderRadius: "8px",
                              p: 2,
                              mb: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                mb: 1,
                              }}
                            >
                              Forwarded Remarks
                            </Typography>

                            <Typography>
                              {clarification.forwareded_message}
                            </Typography>
                          </Box>
                        )}

                        {clarification?.documents &&
                          clarification?.documents?.length > 0 && (
                            <Box
                              sx={{
                                bgcolor: "#F7F7F7",
                                borderRadius: "8px",
                                p: 2,
                                mb: 2,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  mb: 1,
                                }}
                              >
                                Attached Documents
                              </Typography>

                              {clarification.documents.map((doc) => (
                                <Box
                                  key={doc.document_id}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 1,
                                  }}
                                >
                                  <PictureAsPdfIcon
                                    color="error"
                                    fontSize="small"
                                  />

                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      color: "#3A1C73",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                    }}
                                    onClick={() => {
                                      // open/download file
                                      handleViewFile(doc.objectKey);
                                    }}
                                  >
                                    {doc.original_file_name || doc.fileName}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                        {clarification.documents_to_updated.length > 0 && (
                          <Box
                            sx={{
                              bgcolor: "#EFEFF4",
                              borderRadius: "8px",
                              p: 2,
                              mb: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                mb: 2,
                              }}
                            >
                              Documents you need to update
                            </Typography>

                            {clarification.documents_to_updated.map(
                              (doc, index) => (
                                <Typography
                                  key={doc.document_id}
                                  sx={{
                                    fontSize: "14px",
                                    color: "#757575",
                                    mb: 1,
                                  }}
                                >
                                  {index + 1}. {doc.document_name}
                                </Typography>
                              ),
                            )}
                          </Box>
                        )}

                        {clarification?.id === lastConversation?.id &&
                          showSubmitCompliance && (
                            <Button
                              variant="contained"
                              sx={{
                                borderRadius: "20px",
                                bgcolor: "#3a1c73",
                              }}
                              startIcon={<DescriptionOutlinedIcon />}
                              onClick={() => handleComplianceSubmit(clarification)}>
                              Submit Compliance
                            </Button>
                          )}

                        <Divider sx={{ my: 2 }} />
                      </Box>
                    </Box>
                  );
                },
              )}
              {showProvideClarification && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={
                      <ReplyOutlinedIcon
                        sx={{
                          transform: "scaleX(-1)",
                        }}
                      />
                    }
                    sx={{
                      borderRadius: "999px",
                      textTransform: "none",
                      backgroundColor: "#3A1C73",
                    }}
                    onClick={() => setProvideClarificationOpen(true)}
                  >
                    Provide Clarification
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            cards.map((card) => (
              <Box
                key={card.id}
                onClick={() => setSelectedCard(card)}
                sx={{
                  border: "1px solid #CDD1AE",
                  backgroundColor: "#F6F6E8",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1.25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                  cursor: "pointer",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "4px",
                      bgcolor: "#9A951F",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                    }}
                  >
                    <ChatIcon sx={{ fontSize: 14 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 700,
                        fontStyle: "normal",
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "-0.005em",
                        verticalAlign: "bottom",
                        color: "#232323",
                        textAlign: "left",
                      }}
                    >
                      {card.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "11px", color: "#808080", mt: 0.25 }}
                    >
                      {card.date}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: 22,
                    height: 18,
                    borderRadius: "45%",
                    bgcolor: "#B3261D",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {card.count}
                  </Typography>
                </Box>
                <KeyboardArrowRightIcon
                  sx={{
                    fontSize: 20,
                    color: "#666",
                  }}
                />
              </Box>
            ))
          )}
        </Box>
      </Box>
      {lastConversation && (
        <ProvideClarificationDrawer
          open={provideClarificationOpen}
          onClose={() => setProvideClarificationOpen(false)}
          clarification={lastConversation}
          onSuccess={async () => {
            const response = await dispatch(
              fetchSeekClarificationDetailsApplicantApi(accessId),
            );

            setItems(response?.payload?.data || []);
            if (row) fetchRow?.(accessId);
            else fetchAccess?.(accessId);

            setProvideClarificationOpen(false);
          }}
        />
      )}
    </Drawer>
  );
};

export default SeekClarificationTray;
